/**
 * BranchContactManager Component
 *
 * Handles the management interface for branch contacts.
 * Separated from BranchManager for better separation of concerns.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLocalBranches } from '@/hooks/useLocalBranch';
import {
  useLocalBranchContacts,
  useLocalDeleteBranchContact,
} from '@/hooks/useLocalBranchContact';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type { BranchContact } from '@/types/branchContact.types';
import { Building, Edit, MoreHorizontal, Trash2, User } from 'lucide-react';
import React from 'react';

interface BranchContactManagerProps {
  searchTerm?: string;
  className?: string;
  // Dialog control from parent
  onContactDialogOpenChange: (open: boolean) => void;
  // Editing state control from parent
  onEditingContactChange: (contact: BranchContact | null) => void;
}

const BranchContactManager: React.FC<BranchContactManagerProps> = ({
  searchTerm = '',
  className,
  onContactDialogOpenChange,
  onEditingContactChange,
}) => {
  const { t } = useFormsTranslation();

  // Fetch data using hooks
  const { data: contactsData } = useLocalBranchContacts();
  const { data: branchesData } = useLocalBranches();
  const deleteContactMutation = useLocalDeleteBranchContact();

  const displayContacts = Array.isArray(contactsData?.data)
    ? contactsData.data
    : [];

  const availableBranches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : [];

  // Helper function to get branch name by ID
  const getBranchName = (branchId: string): string => {
    const branch = availableBranches.find(b => b.id === branchId);
    return branch ? branch.name : branchId;
  };

  // Filter contacts based on search term
  const filteredContacts = displayContacts.filter(
    (contact: BranchContact) =>
      `${contact.firstName} ${contact.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.function.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Contact handlers
  const handleEditContact = (contact: BranchContact) => {
    onEditingContactChange(contact);
    onContactDialogOpenChange(true);
  };

  const handleDeleteContact = async (contactId: string) => {
    if (window.confirm(t('branchContact.list.confirmDelete'))) {
      await deleteContactMutation.mutateAsync(contactId);
    }
  };

  return (
    <div className={className}>
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('branchContact.list.headers.name')}</TableHead>
              <TableHead>{t('branchContact.list.headers.function')}</TableHead>
              <TableHead>{t('branchContact.list.headers.contact')}</TableHead>
              <TableHead>{t('branchContact.list.headers.branches')}</TableHead>
              <TableHead>
                {t('branchContact.list.headers.representative')}
              </TableHead>
              <TableHead className='text-right'>
                {t('branchContact.list.headers.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className='h-24 text-center'>
                  {searchTerm
                    ? t('branchContact.list.emptyStates.noSearchResults')
                    : t('branchContact.list.emptyStates.noContacts')}
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact: BranchContact) => (
                <TableRow key={contact.id}>
                  <TableCell className='font-medium'>
                    <div className='flex items-center gap-2'>
                      <User className='h-4 w-4 text-muted-foreground' />
                      {contact.firstName} {contact.lastName}
                    </div>
                  </TableCell>
                  <TableCell>{contact.function}</TableCell>
                  <TableCell>
                    <div className='text-sm'>
                      <div>{contact.email}</div>
                      <div className='text-muted-foreground'>
                        {contact.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const branchIds = contact.branchIds || [];
                      const branchCount = branchIds.length;

                      if (branchCount === 0) {
                        return (
                          <span className='text-sm text-muted-foreground'>
                            No branches
                          </span>
                        );
                      }

                      if (branchCount === 1) {
                        return (
                          <div className='flex items-center gap-2'>
                            <Building className='h-4 w-4 text-muted-foreground' />
                            <span className='text-sm'>
                              {getBranchName(branchIds[0])}
                            </span>
                          </div>
                        );
                      }

                      // Multiple branches - show counter and tooltip
                      const firstBranchName = getBranchName(branchIds[0]);
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className='flex items-center gap-2 cursor-help'>
                                <Building className='h-4 w-4 text-muted-foreground' />
                                <Badge
                                  variant='secondary'
                                  className='font-normal'
                                >
                                  {branchCount}
                                </Badge>
                                <span className='text-sm'>
                                  {firstBranchName}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className='space-y-1'>
                              <div className='font-semibold text-xs'>
                                All branches:
                              </div>
                              {branchIds.map(branchId => (
                                <div key={branchId} className='text-xs'>
                                  • {getBranchName(branchId)}
                                </div>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {contact.isRepresentative && (
                      <Badge variant='default'>
                        {t('branchContact.list.labels.representative')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className='text-right'>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='ghost' className='h-8 w-8 p-0'>
                          <MoreHorizontal className='h-4 w-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem
                          onClick={() => handleEditContact(contact)}
                        >
                          <Edit className='mr-2 h-4 w-4' />
                          {t('branchContact.list.actions.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteContact(contact.id)}
                          className='text-destructive'
                        >
                          <Trash2 className='mr-2 h-4 w-4' />
                          {t('branchContact.list.actions.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default BranchContactManager;
